import { useGoogleLogin } from "@react-oauth/google";
import { useState } from "react";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import facebook from "../../../assets/images/Facebook.png";
import google from "../../../assets/images/Google.png";
import signUpLogo from "../../../assets/svg/sign-up-logo.svg";
import {
	validateSignUp,
	validateName,
	validateEmail,
	validateGender,
	validateDob,
	validatePassword,
	validateConfirmPassword,
	getPasswordCriteria,
	getMaxDobDate,
} from "../../../utils/authValidation";
import axios from "../../../components/Hooks/axios";
import { addAccount } from "../../../redux/slices/accountSlice";
import { logOut, setAuth, setUserObject } from "../../../redux/slices/authSlice";
import { setTempEmail } from "../../../redux/slices/tempSlice";
import LoadingBg from "../Loading Bg/LoadingBg";
import classes from "./SignUp.module.scss";

const SignUp = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [gender, setGender] = useState("");
	const [showGender, setShowGender] = useState(false);
	const [DOB, setDOB] = useState("");
	const [password, setPassword] = useState("");
	const [conPassWord, setConPassWord] = useState("");
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [errors, setErrors] = useState({});
	const [touched, setTouched] = useState({});
	const [btnDisabled, setBtnDisabled] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [passIcon1, setPassIcon1] = useState(false);
	const [passIcon2, setPassIcon2] = useState(false);

	const pwdCriteria = getPasswordCriteria(password);

	const handleBlur = (field) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		let err = "";
		if (field === "name") err = validateName(name);
		else if (field === "email") err = validateEmail(email);
		else if (field === "gender") err = validateGender(gender);
		else if (field === "dob") err = validateDob(DOB);
		else if (field === "password") err = validatePassword(password);
		else if (field === "conPassWord") err = validateConfirmPassword(password, conPassWord);

		setErrors((prev) => ({ ...prev, [field]: err }));
	};

	const handleNameChange = (e) => {
		const val = e.target.value;
		setName(val);
		if (touched.name || errors.name) {
			setErrors((prev) => ({ ...prev, name: validateName(val) }));
		}
	};

	const handleEmailChange = (e) => {
		const val = e.target.value;
		setEmail(val);
		if (touched.email || errors.email) {
			setErrors((prev) => ({ ...prev, email: validateEmail(val) }));
		}
	};

	const handleGenderSelect = (selectedGender) => {
		setGender(selectedGender);
		setShowGender(false);
		setTouched((prev) => ({ ...prev, gender: true }));
		if (touched.gender || errors.gender) {
			setErrors((prev) => ({ ...prev, gender: validateGender(selectedGender) }));
		}
	};

	const handleDobChange = (e) => {
		const val = e.target.value;
		setDOB(val);
		setTouched((prev) => ({ ...prev, dob: true }));
		setErrors((prev) => ({ ...prev, dob: validateDob(val) }));
	};

	const handlePasswordChange = (e) => {
		const val = e.target.value;
		setPassword(val);
		if (touched.password || errors.password) {
			setErrors((prev) => ({ ...prev, password: validatePassword(val) }));
		}
		if (touched.conPassWord || conPassWord) {
			setErrors((prev) => ({ ...prev, conPassWord: validateConfirmPassword(val, conPassWord) }));
		}
	};

	const handleConPasswordChange = (e) => {
		const val = e.target.value;
		setConPassWord(val);
		if (touched.conPassWord || errors.conPassWord) {
			setErrors((prev) => ({ ...prev, conPassWord: validateConfirmPassword(password, val) }));
		}
	};

	const handelSubmit = (e) => {
		if (e && e.preventDefault) e.preventDefault();

		const cleanName = name.trim();
		const cleanEmail = email.trim();

		setTouched({
			name: true,
			email: true,
			gender: true,
			dob: true,
			password: true,
			conPassWord: true,
		});

		const { isValid, errors: validationErrors } = validateSignUp({
			name: cleanName,
			email: cleanEmail,
			gender,
			dob: DOB,
			password,
			conPassWord,
			termsAccepted,
		});

		if (!isValid) {
			setErrors(validationErrors);
			const firstError = Object.values(validationErrors)[0];
			if (firstError) toast.warn(firstError);
			return;
		}

		if (!termsAccepted) {
			setErrors((prev) => ({ ...prev, terms: "Please accept the Terms & Conditions to sign up" }));
			toast.warn("Please accept the Terms & Conditions to sign up");
			return;
		}

		setErrors({});
		setBtnDisabled(true);

		const data = JSON.stringify({
			username: cleanName,
			email: cleanEmail,
			dob: DOB,
			gender,
			password: password,
			uniqueName: cleanName + "_" + Date.now(),
		});

		// Clear any previous stale session before starting a new sign up
		dispatch(logOut());

		axios
			.post("/auth/register", data, {
				headers: {
					"Content-Type": "application/json",
				},
			})
			.then(({ data }) => {
				setTimeout(() => {
					toast.success(data.message);
					setBtnDisabled(false);
					if (data.otp) {
						toast.info(`OTP: ${data.otp}`, {
							autoClose: 12000,
						});
					}
					toast.success("SignUp Success");
					dispatch(setTempEmail(cleanEmail));
					navigate("/verification-code", {
						state: {
							signUp: true,
							email: cleanEmail,
							rememberMe,
						},
						replace: true,
					});
				}, 300);
			})
			.catch(({ response }) => {
				const errMsg = response?.data?.error || response?.data?.message || "Sign up failed";
				toast.error(errMsg);
				setBtnDisabled(false);
			});
	};
	// https://www.googleapis.com/oauth2/v3/userinfo?access_token=${tokenResponse.access_token}

	const login = useGoogleLogin({
		onSuccess: (tokenResponse) => {
			axios
				.get(`https://www.googleapis.com/oauth2/v3/userinfo`, {
					headers: {
						Authorization: `Bearer ${tokenResponse.access_token}`,
					},
				})
				.then(({ data }) => {
					axios
						.post("/auth/googleRegister", data)
						.then(({ data }) => {
							if (data.status) {
								dispatch(setAuth({ userId: data.data._id, token: data.token }));
								dispatch(setUserObject(data.data));
								axios.defaults.headers.Authorization = data.token;
								setBtnDisabled(false);
								dispatch(addAccount({ userId: data.data._id, user: data.data, token: data.token }));
								const hasInterests = data.data?.interestName && data.data.interestName.length > 0;
								const destination = (data.isNewUser || !hasInterests) ? "/interest" : "/Home";
								navigate(destination, { replace: true });
							}
						})
						.catch((e) => {
							console.log(e);
							toast.error("Google Sign-Up Failed");
						});
				})
				.catch((e) => console.log(e));
		},
		onError: (tokenResponse) => console.log(tokenResponse),
	});

	const responseFacebook = (response) => {
		if (!response.accessToken) {
			toast.error("Facebook Login was cancelled or failed.");
			return;
		}

		axios
			.post("/auth/facebookRegister", { accessToken: response.accessToken })
			.then(({ data }) => {
				if (data.status) {
					dispatch(setAuth({ userId: data.data._id, token: data.token }));
					dispatch(setUserObject(data.data));
					axios.defaults.headers.Authorization = data.token;
					setBtnDisabled(false);
					dispatch(addAccount({ userId: data.data._id, user: data.data, token: data.token }));
					const hasInterests = data.data?.interestName && data.data.interestName.length > 0;
					const destination = (data.isNewUser || !hasInterests) ? "/interest" : "/Home";
					navigate(destination, { replace: true });
				}
			})
			.catch((e) => {
				toast.error(e.response?.data?.message || "Facebook Sign-Up Failed");
			});
	};

	return (
		<div className={classes["main-container"]}>
			<LoadingBg />
			<div className={classes["sub-container"]}>
				<div className={classes.header}>
					<div className={classes["text-container"]}>
						<p className={classes.heading}>Sign Up</p>
						{/* <p className={classes.text}>
							Lorem ipsum dolor sit amet, consectetur adipiscing elit. Purus est tortor gravida nibh id at.
						</p> */}
					</div>
					<div className={classes.image}>
						<img src={signUpLogo} alt="" />
					</div>
				</div>

				<form className={classes.form} onSubmit={handelSubmit} noValidate>
					<div className={classes["input-container"]}>
						<Link to="/signin" className={classes.ArrowBtn}>
							{">"}
						</Link>

						<div className={classes.input}>
							<label className={classes.label}>Name</label>
							<input
								type="text"
								className={`${classes["input-box"]} ${touched.name && errors.name ? classes.inputError : ""}`}
								value={name}
								placeholder="Enter Your Name"
								onChange={handleNameChange}
								onBlur={() => handleBlur("name")}
							/>
							{touched.name && errors.name && (
								<span className={classes.errorMessage}>{errors.name}</span>
							)}
						</div>

						<div className={classes.input}>
							<label className={classes.label}>E-mail</label>
							<input
								type="email"
								className={`${classes["input-box"]} ${touched.email && errors.email ? classes.inputError : ""}`}
								placeholder="Enter Your Email"
								value={email}
								onChange={handleEmailChange}
								onBlur={() => handleBlur("email")}
							/>
							{touched.email && errors.email && (
								<span className={classes.errorMessage}>{errors.email}</span>
							)}
						</div>

						<div className={classes["contact-info"]}>
							<div className={`${classes.input} ${classes.gender}`}>
								<label className={classes.label}>Gender</label>
								<div className={classes.customSelectContainer}>
									<div 
										className={`${classes["input-box"]} ${touched.gender && errors.gender ? classes.inputError : ""}`}
										onClick={() => setShowGender(!showGender)}
										style={{ 
											cursor: "pointer", 
											display: "flex", 
											justifyContent: "space-between", 
											alignItems: "center", 
											color: gender ? "black" : "#959595",
											fontSize: "1.6rem"
										}}
									>
										{gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : "Select a Gender"}
										<span style={{ fontSize: "1.2rem", transition: "transform 0.3s", transform: showGender ? "rotate(180deg)" : "rotate(0deg)", color: "black" }}>▼</span>
									</div>
									{showGender && (
										<div className={classes.optionsList}>
											<div 
												className={classes.option} 
												onClick={() => handleGenderSelect("male")}
											>
												Male
											</div>
											<div 
												className={classes.option} 
												onClick={() => handleGenderSelect("female")}
											>
												Female
											</div>
										</div>
									)}
								</div>
								{touched.gender && errors.gender && (
									<span className={classes.errorMessage}>{errors.gender}</span>
								)}
							</div>

							<div className={`${classes.input} ${classes.dob}`}>
								<label className={classes.label}>Date of birth</label>
								<input
									type="date"
									max={getMaxDobDate()}
									placeholder="dd-mm-yyyy"
									className={`${classes["input-box"]} ${touched.dob && errors.dob ? classes.inputError : ""}`}
									value={DOB}
									onChange={handleDobChange}
									onBlur={() => handleBlur("dob")}
									onClick={(e) => {
										try {
											e.target.showPicker?.();
										} catch (err) {}
									}}
								/>
								{touched.dob && errors.dob && (
									<span className={classes.errorMessage}>{errors.dob}</span>
								)}
							</div>
						</div>

						<div className={classes.passwordWrapper}>
							<div className={classes.input}>
								<label className={classes.label}>New password</label>

								<div className={`${classes.inputPass} ${touched.password && errors.password ? classes.inputError : ""}`}>
									<input
										type={passIcon1 ? "text" : "password"}
										placeholder="Enter Your Password"
										value={password}
										onChange={handlePasswordChange}
										onBlur={() => handleBlur("password")}
									/>
									<span onClick={() => setPassIcon1(!passIcon1)}>
										{passIcon1 ? <FaEye size={"2.5rem"} /> : <FaEyeSlash size={"2.5rem"} />}
									</span>
								</div>
								{touched.password && errors.password && (
									<span className={classes.errorMessage}>{errors.password}</span>
								)}

								<div className={classes.pwdRequirements}>
									<span className={`${classes.reqItem} ${pwdCriteria.minLength ? classes.valid : classes.invalid}`}>
										{pwdCriteria.minLength ? "✓" : "•"} 8+ chars
									</span>
									<span className={`${classes.reqItem} ${pwdCriteria.hasUppercase ? classes.valid : classes.invalid}`}>
										{pwdCriteria.hasUppercase ? "✓" : "•"} Uppercase
									</span>
									<span className={`${classes.reqItem} ${pwdCriteria.hasNumber ? classes.valid : classes.invalid}`}>
										{pwdCriteria.hasNumber ? "✓" : "•"} Number
									</span>
									<span className={`${classes.reqItem} ${pwdCriteria.hasSpecialChar ? classes.valid : classes.invalid}`}>
										{pwdCriteria.hasSpecialChar ? "✓" : "•"} Special char
									</span>
								</div>
							</div>

							<div className={classes.input}>
								<label className={classes.label}>Re-type password</label>
								<div className={`${classes.inputPass} ${touched.conPassWord && errors.conPassWord ? classes.inputError : ""}`}>
									<input
										type={passIcon2 ? "text" : "password"}
										placeholder="Confirm Password"
										value={conPassWord}
										onChange={handleConPasswordChange}
										onBlur={() => handleBlur("conPassWord")}
									/>
									<span onClick={() => setPassIcon2(!passIcon2)}>
										{passIcon2 ? <FaEye size={"2.5rem"} /> : <FaEyeSlash size={"2.5rem"} />}
									</span>
								</div>
								{touched.conPassWord && errors.conPassWord && (
									<span className={classes.errorMessage}>{errors.conPassWord}</span>
								)}
							</div>
						</div>

						<div className={classes.termsWrapper}>
							<label className={classes.termsLabel}>
								<input
									type="checkbox"
									id="termsCheckboxDesktop"
									checked={termsAccepted}
									onChange={(e) => {
										setTermsAccepted(e.target.checked);
										if (errors.terms) {
											setErrors((prev) => ({ ...prev, terms: "" }));
										}
									}}
									className={classes.termsInput}
								/>
								<span className={`${classes.customCheckbox} ${termsAccepted ? classes.checkboxChecked : ""} ${errors.terms ? classes.checkboxError : ""}`}>
									{termsAccepted && (
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
											<polyline points="20 6 9 17 4 12" />
										</svg>
									)}
								</span>
								<span className={classes.termsText}>
									I agree to the{" "}
									<Link to="/TermsAndCondition" target="_blank" rel="noopener noreferrer" className={classes.termsLink} onClick={(e) => e.stopPropagation()}>
										Terms & Conditions
									</Link>
								</span>
							</label>
							{errors.terms && (
								<span className={classes.errorMessage}>{errors.terms}</span>
							)}
						</div>

						<div className={classes["forget-remember"]}>
							<section className={classes.remember}>
								<input
									type="checkbox"
									className={classes["radio-button"]}
									checked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
									id="rememberMeSignUp"
								/>
								<label htmlFor="rememberMeSignUp" className={classes["remember-me"]}>
									Remember me
								</label>
							</section>
							<div className={classes.actions}>
								<button type="submit" className={classes.btn} disabled={btnDisabled || !termsAccepted}>
									Sign Up
								</button>
							</div>
						</div>
					</div>

					<div className={classes.design}>
						<div className={classes.line}></div>
						<p className={classes.continue}>Continue With</p>
						<div className={classes.line}></div>
					</div>

					<div className={classes.footer}>
						<button type="button" onClick={() => login()} title="Sign up with Google">
							<img src={google} alt="Google" />
						</button>
						<FacebookLogin
							appId={`${process.env.REACT_APP_FACEBOOK_APP_ID}`}
							autoLoad={false}
							scope="email"
							fields="name,email,picture"
							callback={responseFacebook}
							render={(renderProps) => (
								<button type="button" onClick={renderProps.onClick} title="Sign up with Facebook">
									<img src={facebook} alt="Facebook" />
								</button>
							)}
						/>
					</div>
				</form>
			</div>
		</div>
	);
};

export default SignUp;
